/**
 * Shared utilities for managing filters via URL parameters
 * Used by both MapView and SearchView to maintain consistency
 */

import { SetURLSearchParams } from "react-router-dom";

/**
 * Helper function to update a single filter in URL parameters
 * Preserves detail overlay parameters (entity and relationship IDs)
 * Handles all filter types used by both MapView and SearchView
 */
export const updateFilterInURL = (
  filterKey: string,
  value: any,
  searchParams: URLSearchParams,
  setSearchParams: SetURLSearchParams,
  options?: {
    getDbValuesFromDisplayNames?: (values: string[]) => string[];
    getFacilityStatusDbValues?: (values: string[]) => string[];
    getOrgCategoryDbValues?: (values: string[]) => string[];
  }
) => {
  const params = new URLSearchParams(searchParams);

  // Preserve detail overlay params
  const entityId = searchParams.get("detail_entity_id");
  const relationshipId = searchParams.get("detail_relationship_id");

  // Handle different filter types
  if (filterKey === "searchTerm" || filterKey === "search") {
    if (value) {
      params.set("search", value);
    } else {
      params.delete("search");
    }
  } else if (filterKey === "entityType") {
    if (value && value !== "organizations") {
      params.set("entityType", value);
    } else {
      params.delete("entityType");
    }
  } else if (filterKey === "facilityTypes") {
    if (value.length > 0) {
      // Convert display names to DB values if converter provided
      const dbValues = options?.getDbValuesFromDisplayNames
        ? options.getDbValuesFromDisplayNames(value)
        : value;
      params.set("facilityTypes", dbValues.join(","));
    } else {
      params.delete("facilityTypes");
    }
  } else if (filterKey === "organizationTypes") {
    if (value.length > 0) {
      // Convert display names to DB values if converter provided
      const dbValues = options?.getOrgCategoryDbValues
        ? options.getOrgCategoryDbValues(value)
        : value;
      params.set("organizationTypes", dbValues.join(","));
    } else {
      params.delete("organizationTypes");
    }
  } else if (filterKey === "labels") {
    if (value.length > 0) {
      params.set("labels", value.join(","));
    } else {
      params.delete("labels");
    }
  } else if (filterKey === "dateRange") {
    if (value.start) {
      params.set("dateStart", value.start);
    } else {
      params.delete("dateStart");
    }
    if (value.end) {
      params.set("dateEnd", value.end);
    } else {
      params.delete("dateEnd");
    }
  } else if (filterKey === "customFilters") {
    // Handle nested customFilters object
    if (value.homeCountries?.length > 0) {
      params.set("homeCountries", value.homeCountries.join(","));
    } else {
      params.delete("homeCountries");
    }
    if (value.naicsCodes?.length > 0) {
      params.set("naics", value.naicsCodes.join(","));
    } else {
      params.delete("naics");
    }
    if (value.inputHSCodes?.length > 0) {
      params.set("hsInput", value.inputHSCodes.join(","));
    } else {
      params.delete("hsInput");
    }
    if (value.outputHSCodes?.length > 0) {
      params.set("hsOutput", value.outputHSCodes.join(","));
    } else {
      params.delete("hsOutput");
    }
    if (value.facilityStatuses?.length > 0) {
      // Convert display names to DB values if converter provided
      const dbValues = options?.getFacilityStatusDbValues
        ? options.getFacilityStatusDbValues(value.facilityStatuses)
        : value.facilityStatuses;
      params.set("facilityStatuses", dbValues.join(","));
    } else {
      params.delete("facilityStatuses");
    }
    if (value.facilityOwners?.length > 0) {
      params.set("facilityOwners", value.facilityOwners.join(","));
    } else {
      params.delete("facilityOwners");
    }
    if (value.suppliers?.length > 0) {
      params.set("suppliers", value.suppliers.join(","));
    } else {
      params.delete("suppliers");
    }
    if (value.customers?.length > 0) {
      params.set("customers", value.customers.join(","));
    } else {
      params.delete("customers");
    }
    if (value.foundingYearMin) {
      params.set("foundingYearMin", value.foundingYearMin);
    } else {
      params.delete("foundingYearMin");
    }
    if (value.foundingYearMax) {
      params.set("foundingYearMax", value.foundingYearMax);
    } else {
      params.delete("foundingYearMax");
    }
    if (value.keywords?.length > 0) {
      params.set("keywords", value.keywords.join(","));
    } else {
      params.delete("keywords");
    }
    if (value.projectTypes?.length > 0) {
      params.set("projectTypes", value.projectTypes.join(","));
    } else {
      params.delete("projectTypes");
    }
    if (value.projectStatuses?.length > 0) {
      params.set("projectStatuses", value.projectStatuses.join(","));
    } else {
      params.delete("projectStatuses");
    }
  } else if (filterKey === "facilityOwnerFilter") {
    if (value.ownerCompanies?.length > 0) {
      params.set(
        "facilityOwnerCompanies",
        JSON.stringify(value.ownerCompanies)
      );
      params.set("facilityOwnerMaxTiers", value.maxTiers.toString());
    } else {
      params.delete("facilityOwnerCompanies");
      params.delete("facilityOwnerMaxTiers");
    }
  } else if (filterKey === "relationshipFilters") {
    if (value?.length > 0) {
      params.set("relationships", encodeURIComponent(JSON.stringify(value)));
    } else {
      params.delete("relationships");
    }
  } else if (filterKey === "projectOrganizationFilter") {
    if (value.participantCompanies?.length > 0) {
      params.set(
        "projectParticipants",
        encodeURIComponent(JSON.stringify(value))
      );
    } else {
      params.delete("projectParticipants");
    }
  } else if (Array.isArray(value)) {
    // Generic array handling
    if (value.length > 0) {
      params.set(filterKey, value.join(","));
    } else {
      params.delete(filterKey);
    }
  } else if (value !== null && value !== undefined && value !== "") {
    // Generic value handling
    params.set(filterKey, value.toString());
  } else {
    params.delete(filterKey);
  }

  // Restore detail overlay params
  if (entityId) params.set("detail_entity_id", entityId);
  if (relationshipId) params.set("detail_relationship_id", relationshipId);

  setSearchParams(params, { replace: true });
};

/**
 * Clear all filters from URL while preserving entity type and detail overlays
 */
export const clearAllFiltersFromURL = (
  currentEntityType: string,
  searchParams: URLSearchParams,
  setSearchParams: SetURLSearchParams
) => {
  const params = new URLSearchParams();

  // Preserve entity type if not default
  if (currentEntityType && currentEntityType !== "organizations") {
    params.set("entityType", currentEntityType);
  }

  // Preserve detail overlay params
  const entityId = searchParams.get("detail_entity_id");
  const relationshipId = searchParams.get("detail_relationship_id");
  if (entityId) params.set("detail_entity_id", entityId);
  if (relationshipId) params.set("detail_relationship_id", relationshipId);

  // Note: Graph settings (seed_entity, org_structure, supplier_tiers, customer_tiers) are cleared
  // Layout parameters (layoutType, showEdgeLabels, showNodeLabels) are not URL-synced, so no action needed

  setSearchParams(params, { replace: true });
};

/**
 * Extract URL params for saving (excluding detail view params)
 */
export const getSearchParamsForSave = (
  searchParams: URLSearchParams
): Record<string, string> => {
  const params: Record<string, string> = {};
  searchParams.forEach((value, key) => {
    // Exclude detail view parameters
    if (key !== "detail_entity_id" && key !== "detail_relationship_id") {
      params[key] = value;
    }
  });
  return params;
};
